"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaFilter,
  FaBuilding,
  FaToggleOn,
  FaToggleOff,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFileExcel,
  FaSpinner,
} from "react-icons/fa"
import { FiFilter, FiX, FiRefreshCw, FiSearch as FiSearchIcon, FiUser, FiBookOpen, FiMapPin, FiCalendar } from "react-icons/fi"
import { Pagination as PaginationComponent } from "../../../utils/pagination.utils"
import { TrainingInstitute, Pagination } from "../types"
import type { SelectOption } from "../../../utils/select-with-search"
import { SearchableSelect } from "../../../utils/select-with-search"
import { useAuth } from "../../../provider/auth.provider"
import { ExcelDownload } from "../../../utils/excel.utils"
import toast from "react-hot-toast"
import api from "../../../libs/axios"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

export interface InstituteFilters {
  [key: string]: string | undefined
  search?: string
  institute_name?: string
  institute_short_name?: string
  address?: string
  contact_person?: string
  contact_phone?: string
  contact_email?: string
  gst_number?: string
  is_active?: string
  created_from?: string
  created_to?: string
}

type TrainingInstituteExportRow = Record<string, string | number | boolean | null>

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: InstituteFilters
  onFiltersChange: (filters: InstituteFilters) => void
  onApply: () => void
  onReset: () => void
  instituteOptions: SelectOption[]
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, filters, onFiltersChange, onApply, onReset, instituteOptions }) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null)

  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic")
    }
  }, [isOpen])

  useEffect(() => {
    if (!filters.institute_name) {
      setSelectedInstitute(null)
      return
    }
    const matchedOption = instituteOptions.find((option) => option.value === filters.institute_name)
    if (matchedOption) {
      setSelectedInstitute(matchedOption)
    } else {
      setSelectedInstitute({ id: 0, label: filters.institute_name, value: filters.institute_name })
    }
  }, [filters.institute_name, instituteOptions])

  if (!isOpen) return null

  const handleInputChange = (field: keyof InstituteFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const tabs = [
    { id: "basic", label: "Basic Info", icon: FiSearchIcon },
    { id: "contact", label: "Contact", icon: FiUser },
    { id: "details", label: "Details", icon: FiBookOpen },
    { id: "location", label: "Location", icon: FiMapPin },
    { id: "dates", label: "Dates", icon: FiCalendar },
  ]

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search institutes..."
                  value={filters.search || ""}
                  onChange={(e) => handleInputChange("search", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Institute Name</label>
                {/* <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Institute name"
                  value={filters.institute_name || ""}
                  onChange={(e) => handleInputChange("institute_name", e.target.value)}
                /> */}
                <SearchableSelect
                  value={selectedInstitute}
                  onChange={(option) => handleInputChange("institute_name", option ? String(option.value) : "")}
                  options={instituteOptions}
                  placeholder="Search institutes"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Short Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Short name"
                  value={filters.institute_short_name || ""}
                  onChange={(e) => handleInputChange("institute_short_name", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.is_active || ""}
                  onChange={(e) => handleInputChange("is_active", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Contact person"
                  value={filters.contact_person || ""}
                  onChange={(e) => handleInputChange("contact_person", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Contact phone"
                  value={filters.contact_phone || ""}
                  onChange={(e) => handleInputChange("contact_phone", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Contact email"
                  value={filters.contact_email || ""}
                  onChange={(e) => handleInputChange("contact_email", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "details" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="GST Number"
                  value={filters.gst_number || ""}
                  onChange={(e) => handleInputChange("gst_number", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "location" && (
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Address"
                  value={filters.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ""}
                  onChange={(e) => handleInputChange("created_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ""}
                  onChange={(e) => handleInputChange("created_to", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface InstitutesListProps {
  institutes: TrainingInstitute[]
  pagination: Pagination
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdd: () => void
  onEdit: (institute: TrainingInstitute) => void
  onToggleStatus: (institute: TrainingInstitute) => void
  loading?: boolean
  sortConfig?: { field: string; direction: "ASC" | "DESC" }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  onAdvancedFilter?: (filters: InstituteFilters) => void
  advancedFilters?: InstituteFilters
  instituteOptions?: SelectOption[]
}

export const InstitutesList: React.FC<InstitutesListProps> = ({
  institutes,
  pagination,
  onSearch,
  onStatusFilter,
  onPageChange,
  onLimitChange,
  onAdd,
  onEdit,
  onToggleStatus,
  loading = false,
  sortConfig,
  onSort,
  onAdvancedFilter,
  advancedFilters = {},
  instituteOptions = [],
}) => {
  const { hasPermission } = useAuth()
  const canCreateInstitute = hasPermission("TrainingInstitute", "create")
  const canEditInstitute = hasPermission("TrainingInstitute", "edit")
  const canToggleStatusInstitute = hasPermission("TrainingInstitute", "toggle_status")

  const [searchTerm, setSearchTerm] = useState("")
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)
  const [statusFilter, setStatusFilter] = useState("")
  const [filters, setFilters] = useState<InstituteFilters>(advancedFilters)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<TrainingInstituteExportRow[]>([])

  useEffect(() => {
    setFilters(advancedFilters)
  }, [advancedFilters])

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters).filter(([_, value]) => value !== undefined && value !== "").length

  const handleFiltersChange = (updatedFilters: InstituteFilters) => {
    setFilters(updatedFilters)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
    setIsFilterModalOpen(false)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setStatusFilter(value)
    onStatusFilter(value)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatDateTimeValue = (dateString?: string | null) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusLabel = (value?: boolean) => (value ? "Active" : "Inactive")

  const getCreatorName = (creator?: { first_name?: string; last_name?: string } | null) => {
    if (!creator) return "-"
    return [creator.first_name, creator.last_name].filter(Boolean).join(" ") || "-"
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number | boolean> = { page, limit }

    if (searchTerm.trim()) params.search = searchTerm.trim()
    if (statusFilter) params.is_active = statusFilter

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value
      }
    })

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const mapInstituteForExport = (institute: TrainingInstitute): TrainingInstituteExportRow => {
    return {
      "Institute Name": institute.institute_name,
      "Short Name": institute.institute_short_name || "-",
      Address: institute.address || "-",
      "Contact Person": institute.contact_person || "-",
      "Contact Phone": institute.contact_phone || "-",
      "Contact Email": institute.contact_email || "-",
      "GST Number": institute.gst_number || "-",
      Status: getStatusLabel(institute.is_active),
      "Created By": getCreatorName(institute.creator),
      "Creator Email": institute.creator?.email || "-",
      "Created At": formatDateTimeValue(institute.created_at),
      "Updated At": formatDateTimeValue(institute.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (exporting) return

    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allInstitutes: TrainingInstitute[] = []

      while (hasMore) {
        const params = buildExportParams(page, pageSize)
        const response = await api.get("/training/institutes/list", { params })

        if (!response.data?.success) {
          throw new Error(response.data?.message || "Failed to fetch institutes")
        }

        const data = response.data.data || {}
        const fetched: TrainingInstitute[] = data.institutes || []
        const paginationInfo = data.pagination

        allInstitutes.push(...fetched)

        const nextPageAvailable = paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false
        } else {
          page += 1
        }
      }

      if (!allInstitutes.length) {
        toast.error("No institutes found for export")
        return
      }

      setExportData(allInstitutes.map(mapInstituteForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error exporting institutes:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare export data")
    } finally {
      setExporting(false)
    }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction = sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig?.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    if (sortConfig.direction === "ASC") return <FaSortUp className="w-3 h-3 text-blue-600" />
    return <FaSortDown className="w-3 h-3 text-blue-600" />
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Training Institutes</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              {exporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export Excel
                </>
              )}
            </button>
            {canCreateInstitute && (
              <button
                onClick={onAdd}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add Institute
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by name, contact..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="w-full sm:w-auto flex items-end">
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${
                getActiveFiltersCount() > 0 ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("institute_name")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Institute Name {getSortIcon("institute_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("institute_short_name")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Short Name {getSortIcon("institute_short_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("contact_person")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Contact Person {getSortIcon("contact_person")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("contact_phone")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Contact Phone {getSortIcon("contact_phone")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("contact_email")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Email {getSortIcon("contact_email")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[80px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("is_active")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Status {getSortIcon("is_active")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button
                  type="button"
                  onClick={() => handleColumnSort("created_at")}
                  className="flex items-center gap-2 text-left uppercase tracking-wider"
                >
                  Created At {getSortIcon("created_at")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading institutes...</span>
                  </div>
                </td>
              </tr>
            ) : institutes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaBuilding className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No institutes found</p>
                  </div>
                </td>
              </tr>
            ) : (
              institutes.map((institute) => (
                <tr key={institute.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {canEditInstitute && (
                        <button
                          onClick={() => onEdit(institute)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {canToggleStatusInstitute && (
                        <button
                          onClick={() => onToggleStatus(institute)}
                          className={`p-2 rounded transition-colors ${institute.is_active ? 'text-green-600 hover:text-green-800 hover:bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                          title={institute.is_active ? "Deactivate" : "Activate"}
                        >
                          {institute.is_active ? <FaToggleOn className="w-5 h-5" /> : <FaToggleOff className="w-5 h-5" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className="text-sm font-semibold text-gray-900">{institute.institute_name}</p>
                    {institute.address && <p className="text-xs text-gray-500 truncate max-w-[200px]">{institute.address}</p>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{institute.institute_short_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{institute.contact_person || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{institute.contact_phone || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{institute.contact_email || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${institute.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {institute.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(institute.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationComponent pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        instituteOptions={instituteOptions}
      />
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="training_institutes"
        title="Export Training Institutes"
        description="Download comprehensive training institute details"
      />
    </div>
  )
}
